import React, { useMemo } from 'react';

import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import { FaArrowLeft } from 'react-icons/fa';
import { useParams } from 'react-router-dom';

import { LoadingIndicator } from '@src/components/atoms/loading-indicator';
import { TagRounded } from '@src/components/atoms/tag-rounded';
import { IconTextLinkRounded } from '@src/components/molecules/icon-text-link-rounded';
import { NoItemsFound } from '@src/components/molecules/no-items-found';
import { RelatedPosts } from '@src/components/molecules/related-posts';
import { useGetPostBySlugQuery, useGetPostsQuery } from '@src/store/queries/posts';
import { PageTitle, Paragraph1 } from '@src/theme';
import { bemClassName } from '@src/utils/bem';
import { canonical, ogImage, ogUrl } from '@src/utils/seo';
import { articleSchema } from '@src/utils/seo-schemas';
import { useSSRData } from '@src/utils/ssr-data-context';

import { StyledBlogPostPage } from './styles';

const b = bemClassName('blog-post-page');

const RELATED_POSTS_LIMIT = 3;

const BlogPostPage = () => {
  const { slug } = useParams();
  const ssrData = useSSRData();

  const ssrPost = ssrData?.postData?.slug === slug ? ssrData.postData : null;
  const { data: fetchedPost, isLoading, error } = useGetPostBySlugQuery(slug, { skip: !!ssrPost });

  const post = ssrPost || fetchedPost;

  const relatedFilter = useMemo(
    () => (post?.category ? { category: post.category, limit: RELATED_POSTS_LIMIT + 1, page: 1 } : undefined),
    [post?.category],
  );
  const { data: relatedData } = useGetPostsQuery(relatedFilter, { skip: !post?.category || !!ssrData?.relatedPosts });

  const relatedPosts = useMemo(() => {
    if (ssrData?.relatedPosts) {
      return ssrData.relatedPosts.filter((p) => p.slug !== slug).slice(0, RELATED_POSTS_LIMIT);
    }

    if (!relatedData?.data) return [];

    return relatedData.data.filter((p) => p.slug !== slug).slice(0, RELATED_POSTS_LIMIT);
  }, [ssrData?.relatedPosts, relatedData?.data, slug]);

  if (!ssrPost && isLoading) {
    return (
      <StyledBlogPostPage>
        <div className={b()}>
          <LoadingIndicator size={16} />
        </div>
      </StyledBlogPostPage>
    );
  }

  if (error || !post) {
    return (
      <StyledBlogPostPage>
        <div className={b()}>
          <NoItemsFound />
        </div>
      </StyledBlogPostPage>
    );
  }

  const canSanitize = typeof DOMPurify.sanitize === 'function';
  const sanitizedContent = canSanitize ? DOMPurify.sanitize(post.content, { ADD_ATTR: ['class'] }) : post.content;

  return (
    <StyledBlogPostPage>
      <Helmet>
        <title>{post.title} — Active Shape Blog</title>
        <meta name="description" content={post.excerpt || post.title} />
        {canonical(`/blog/${slug}`) && <link rel="canonical" href={canonical(`/blog/${slug}`)} />}
        <meta property="og:site_name" content="Active Shape" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        {(post.coverImageUrl || ogImage()) && <meta property="og:image" content={post.coverImageUrl || ogImage()} />}
        {ogUrl(`/blog/${slug}`) && <meta property="og:url" content={ogUrl(`/blog/${slug}`)} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || post.title} />
        {(post.coverImageUrl || ogImage()) && <meta name="twitter:image" content={post.coverImageUrl || ogImage()} />}
        <script type="application/ld+json">{JSON.stringify(articleSchema(post, slug))}</script>
      </Helmet>
      <div className={b()}>
        <div className={b('back')}>
          <IconTextLinkRounded
            icon={<FaArrowLeft />}
            text="Back to Blog"
            link="/blog"
            iconSize={32}
            ariaLabel="Back to Blog"
          />
        </div>
        {post.coverImageUrl && (
          <div className={b('cover-image')}>
            <img src={post.coverImageUrl} alt={post.title} />
          </div>
        )}

        <div className={b('header')}>
          <div className={b('header', 'category')}>
            <TagRounded text={post.category} />
          </div>
          <div className={b('header', 'title')}>
            <PageTitle>{post.title}</PageTitle>
          </div>
          {post.excerpt && (
            <div className={b('header', 'excerpt')}>
              <Paragraph1>{post.excerpt}</Paragraph1>
            </div>
          )}
          <div className={b('header', 'tags')}>
            {post.tags?.map((tag, index) => (
              <div key={`post-tag-${index}`} className={b('header', 'tags', 'item')}>
                <TagRounded text={tag} />
              </div>
            ))}
          </div>
        </div>

        <div className={b('content')} dangerouslySetInnerHTML={{ __html: sanitizedContent }} />

        <RelatedPosts posts={relatedPosts} />
      </div>
    </StyledBlogPostPage>
  );
};

export default BlogPostPage;
